/**
 * Sound effects, synthesized with the Web Audio API. Each effect is a short recipe of oscillators
 * and filtered noise. The one exception is the table's own joke: the song that plays when a magus
 * critically misses a Spellstrike is a recording (assets/magus-spellstrike-fumble.m4a), fetched the
 * first time audio is unlocked and played through the same volume control. The audio context starts
 * on the first click or key press, as browsers require.
 */

export type SoundName =
  | 'dice' | 'hit' | 'crit' | 'miss' | 'hurt' | 'death' | 'heal' | 'spell' | 'spell-fail'
  | 'light' | 'light-fumble' | 'light-out' | 'door-open' | 'door-close' | 'loot' | 'drop'
  | 'encounter' | 'victory' | 'turn' | 'your-turn' | 'level-up' | 'listen' | 'shrine' | 'good' | 'bad' | 'ominous'
  | 'fall' | 'splash' | 'glyph' | 'rubble' | 'unlock' | 'speech' | 'quest' | 'spellstrike-fumble';

/** Recorded clips, by the sound that plays them. */
const CLIPS: Partial<Record<SoundName, string>> = { 'spellstrike-fumble': 'assets/magus-spellstrike-fumble.m4a' };

export class SoundBoard {
  private ac: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private volumeValue: number;
  private mutedValue: boolean;
  private readonly clips = new Map<SoundName, Promise<AudioBuffer | null>>();
  /** A clip that is playing (or about to): one at a time, so a run of fumbles doesn't pile the song on itself. */
  private clipPlaying: AudioBufferSourceNode | null = null;

  constructor() {
    this.volumeValue = Number(read('crawler.volume') ?? 0.6);
    this.mutedValue = read('crawler.muted') === '1';
    const unlock = () => {
      this.context();
      for (const name of Object.keys(CLIPS) as SoundName[]) void this.clip(name);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  get volume(): number {
    return this.volumeValue;
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  setVolume(v: number): void {
    this.volumeValue = Math.max(0, Math.min(1, v));
    write('crawler.volume', String(this.volumeValue));
    this.applyGain();
  }

  setMuted(m: boolean): void {
    this.mutedValue = m;
    write('crawler.muted', m ? '1' : '0');
    this.applyGain();
  }

  /** Plays an effect, optionally after `delayMs`. Silently does nothing before audio is unlocked. */
  play(name: SoundName, delayMs = 0, intensity = 1): void {
    if (this.mutedValue || this.volumeValue <= 0) return;
    const ac = this.ac;
    if (!ac || ac.state !== 'running') return;
    const t = ac.currentTime + Math.max(0, delayMs) / 1000 + 0.01;
    if (CLIPS[name]) {
      void this.playClip(name, t).then((played) => {
        if (!played) RECIPES[name](this, Math.max(t, this.ac?.currentTime ?? t), intensity);
      });
      return;
    }
    RECIPES[name](this, t, intensity);
  }

  /** The decoded recording for a sound, fetched once (null if the browser can't fetch or decode it). */
  private clip(name: SoundName): Promise<AudioBuffer | null> {
    let pending = this.clips.get(name);
    if (!pending) {
      pending = (async () => {
        const ac = this.ac;
        const url = CLIPS[name];
        if (!ac || !url) return null;
        try {
          const res = await fetch(url);
          if (!res.ok) return null;
          return await ac.decodeAudioData(await res.arrayBuffer());
        } catch {
          return null;
        }
      })();
      this.clips.set(name, pending);
    }
    return pending;
  }

  /** Plays a recording at `t` (audio-context time), stopping one already playing. False if there was nothing to play. */
  private async playClip(name: SoundName, t: number): Promise<boolean> {
    const buffer = await this.clip(name);
    const ac = this.ac;
    if (!ac || !this.master || this.mutedValue) return true;
    if (!buffer) return false;
    this.clipPlaying?.stop();
    const src = ac.createBufferSource();
    src.buffer = buffer;
    // Recordings are louder than the synthesized stings: bring the clip down to their level.
    const g = ac.createGain();
    g.gain.value = 0.6;
    src.connect(g).connect(this.master);
    src.onended = () => { if (this.clipPlaying === src) this.clipPlaying = null; };
    this.clipPlaying = src;
    src.start(Math.max(t, ac.currentTime));
    return true;
  }

  // ------------------------------------------------------------ building blocks

  private context(): AudioContext | null {
    if (!this.ac) {
      try {
        this.ac = new AudioContext();
      } catch {
        return null;
      }
      this.master = this.ac.createGain();
      this.master.connect(this.ac.destination);
      this.applyGain();
    }
    if (this.ac.state === 'suspended') void this.ac.resume();
    return this.ac;
  }

  private applyGain(): void {
    if (this.master) this.master.gain.value = this.mutedValue ? 0 : this.volumeValue * 0.5;
  }

  get out(): GainNode {
    return this.master!;
  }

  get audio(): AudioContext {
    return this.ac!;
  }

  noise(): AudioBuffer {
    if (!this.noiseBuffer) {
      const ac = this.ac!;
      this.noiseBuffer = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    }
    return this.noiseBuffer;
  }

  /** An oscillator with a quick attack and exponential decay. */
  tone(t: number, opts: { type?: OscillatorType; freq: number; to?: number; dur: number; gain: number; attack?: number; filter?: number }): void {
    const ac = this.ac!;
    const osc = ac.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(opts.freq, t);
    if (opts.to) osc.frequency.exponentialRampToValueAtTime(opts.to, t + opts.dur);
    const g = envelope(ac, t, opts.gain, opts.attack ?? 0.005, opts.dur);
    let node: AudioNode = osc;
    if (opts.filter) {
      const f = ac.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = opts.filter;
      osc.connect(f);
      node = f;
    }
    node.connect(g).connect(this.master!);
    osc.start(t);
    osc.stop(t + opts.dur + 0.05);
  }

  /** Filtered noise: clicks, thuds, whooshes, hisses. */
  burst(t: number, opts: { dur: number; gain: number; type: BiquadFilterType; freq: number; to?: number; q?: number; attack?: number }): void {
    const ac = this.ac!;
    const src = ac.createBufferSource();
    src.buffer = this.noise();
    src.loop = true;
    const f = ac.createBiquadFilter();
    f.type = opts.type;
    f.Q.value = opts.q ?? 1;
    f.frequency.setValueAtTime(opts.freq, t);
    if (opts.to) f.frequency.exponentialRampToValueAtTime(opts.to, t + opts.dur);
    const g = envelope(ac, t, opts.gain, opts.attack ?? 0.003, opts.dur);
    src.connect(f).connect(g).connect(this.master!);
    src.start(t, Math.random() * 0.5);
    src.stop(t + opts.dur + 0.05);
  }
}

function envelope(ac: AudioContext, t: number, peak: number, attack: number, dur: number): GainNode {
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(attack + 0.01, dur));
  return g;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable (private mode); the setting just won't persist.
  }
}

const notes = (s: SoundBoard, t: number, freqs: number[], step: number, opts: { type?: OscillatorType; dur: number; gain: number }) =>
  freqs.forEach((freq, i) => s.tone(t + i * step, { freq, ...opts }));

const RECIPES: Record<SoundName, (s: SoundBoard, t: number, intensity: number) => void> = {
  dice(s, t, n) {
    const clicks = Math.min(9, 3 + Math.round(n * 1.5));
    let at = t;
    for (let i = 0; i < clicks; i++) {
      s.burst(at, { dur: 0.025, gain: 0.5 * (1 - i / (clicks + 2)), type: 'bandpass', freq: 1800 + Math.random() * 2200, q: 4 });
      if (i % 3 === 0) s.tone(at, { freq: 160 + Math.random() * 60, dur: 0.05, gain: 0.25 });
      at += 0.04 + Math.random() * 0.07 + i * 0.012;
    }
  },
  hit(s, t) {
    s.tone(t, { freq: 120, to: 50, dur: 0.18, gain: 0.7 });
    s.burst(t, { dur: 0.09, gain: 0.5, type: 'lowpass', freq: 1200, to: 300 });
  },
  crit(s, t) {
    RECIPES.hit(s, t, 1);
    s.tone(t + 0.02, { type: 'triangle', freq: 1320, dur: 0.6, gain: 0.18 });
    s.tone(t + 0.02, { type: 'triangle', freq: 1980, dur: 0.45, gain: 0.1 });
  },
  miss(s, t) {
    s.burst(t, { dur: 0.22, gain: 0.35, type: 'bandpass', freq: 500, to: 2600, q: 2, attack: 0.06 });
  },
  hurt(s, t) {
    s.tone(t, { freq: 90, to: 40, dur: 0.3, gain: 0.8 });
    s.burst(t, { dur: 0.15, gain: 0.45, type: 'lowpass', freq: 700, to: 150 });
  },
  death(s, t) {
    s.tone(t, { type: 'sawtooth', freq: 220, to: 45, dur: 0.7, gain: 0.25, filter: 900 });
    s.burst(t + 0.25, { dur: 0.25, gain: 0.4, type: 'lowpass', freq: 500, to: 120 });
  },
  heal(s, t) {
    notes(s, t, [523, 659, 784, 1047], 0.07, { dur: 0.35, gain: 0.16 });
  },
  spell(s, t) {
    for (const [i, f] of [440, 660, 990].entries()) s.tone(t + i * 0.04, { freq: f, to: f * 1.5, dur: 0.45, gain: 0.12, attack: 0.05 });
    s.burst(t, { dur: 0.4, gain: 0.12, type: 'highpass', freq: 5000, attack: 0.08 });
  },
  'spell-fail'(s, t) {
    s.tone(t, { type: 'sawtooth', freq: 420, to: 110, dur: 0.4, gain: 0.15, filter: 1400 });
    s.burst(t + 0.15, { dur: 0.35, gain: 0.2, type: 'highpass', freq: 2500, to: 6000 });
  },
  light(s, t) {
    s.burst(t, { dur: 0.08, gain: 0.35, type: 'bandpass', freq: 3500, q: 3 });
    s.burst(t + 0.06, { dur: 0.45, gain: 0.4, type: 'lowpass', freq: 300, to: 2800, attack: 0.08 });
  },
  'light-fumble'(s, t) {
    s.burst(t, { dur: 0.04, gain: 0.35, type: 'bandpass', freq: 3800, q: 5 });
    s.burst(t + 0.14, { dur: 0.04, gain: 0.3, type: 'bandpass', freq: 3200, q: 5 });
  },
  'light-out'(s, t) {
    s.burst(t, { dur: 0.6, gain: 0.3, type: 'highpass', freq: 2500, to: 6000, attack: 0.02 });
  },
  'door-open'(s, t) {
    const ac = s.audio;
    const osc = ac.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(130, t);
    osc.frequency.linearRampToValueAtTime(190, t + 0.5);
    const lfo = ac.createOscillator();
    const depth = ac.createGain();
    lfo.frequency.value = 14;
    depth.gain.value = 18;
    lfo.connect(depth).connect(osc.frequency);
    const f = ac.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = 900;
    f.Q.value = 3;
    const g = envelope(ac, t, 0.18, 0.05, 0.55);
    osc.connect(f).connect(g).connect(s.out);
    osc.start(t);
    lfo.start(t);
    osc.stop(t + 0.6);
    lfo.stop(t + 0.6);
    s.tone(t + 0.5, { freq: 110, to: 70, dur: 0.12, gain: 0.35 });
  },
  'door-close'(s, t) {
    s.tone(t, { freq: 120, to: 60, dur: 0.15, gain: 0.6 });
    s.burst(t, { dur: 0.07, gain: 0.4, type: 'lowpass', freq: 900 });
  },
  loot(s, t) {
    notes(s, t, [2400, 3000, 2700], 0.06, { dur: 0.18, gain: 0.12 });
  },
  drop(s, t) {
    s.burst(t, { dur: 0.08, gain: 0.35, type: 'lowpass', freq: 1500, to: 400 });
    s.tone(t, { freq: 300, to: 180, dur: 0.06, gain: 0.15 });
  },
  encounter(s, t) {
    s.tone(t, { freq: 70, to: 45, dur: 0.4, gain: 0.9 });
    s.tone(t + 0.28, { freq: 70, to: 45, dur: 0.4, gain: 0.9 });
    s.tone(t + 0.1, { type: 'sawtooth', freq: 147, dur: 0.9, gain: 0.12, attack: 0.15, filter: 700 });
  },
  victory(s, t) {
    notes(s, t, [392, 523, 659, 784], 0.11, { type: 'triangle', dur: 0.4, gain: 0.18 });
  },
  turn(s, t) {
    notes(s, t, [659, 880], 0.12, { dur: 0.35, gain: 0.14 });
  },
  // Your turn: a bright rising bell call over a soft low strike, longer and louder than a prompt's chime.
  'your-turn'(s, t) {
    s.tone(t, { type: 'triangle', freq: 196, dur: 0.5, gain: 0.16 });
    notes(s, t, [784, 988, 1319], 0.09, { type: 'triangle', dur: 0.5, gain: 0.16 });
    s.tone(t + 0.18, { type: 'sine', freq: 2638, dur: 0.6, gain: 0.04, attack: 0.02 });
  },
  'level-up'(s, t) {
    notes(s, t, [523, 659, 784, 1047, 1319], 0.08, { type: 'triangle', dur: 0.45, gain: 0.16 });
    s.burst(t + 0.3, { dur: 0.5, gain: 0.08, type: 'highpass', freq: 6000 });
  },
  listen(s, t) {
    s.burst(t, { dur: 0.6, gain: 0.25, type: 'lowpass', freq: 260, attack: 0.15 });
  },
  shrine(s, t) {
    for (const f of [262, 330, 392]) s.tone(t, { freq: f, dur: 1.1, gain: 0.08, attack: 0.25 });
  },
  good(s, t) {
    s.tone(t, { freq: 880, dur: 0.12, gain: 0.12 });
    s.tone(t + 0.07, { freq: 1320, dur: 0.16, gain: 0.1 });
  },
  bad(s, t) {
    s.tone(t, { type: 'triangle', freq: 196, to: 150, dur: 0.22, gain: 0.18 });
  },
  fall(s, t) {
    s.burst(t, { dur: 0.3, gain: 0.3, type: 'bandpass', freq: 1400, to: 300, q: 2, attack: 0.05 });
    RECIPES.hurt(s, t + 0.3, 1);
  },
  splash(s, t) {
    s.burst(t, { dur: 0.45, gain: 0.45, type: 'lowpass', freq: 2400, to: 400, attack: 0.01 });
    for (let i = 0; i < 4; i++) s.tone(t + 0.1 + i * 0.07, { freq: 500 + Math.random() * 500, to: 900 + Math.random() * 400, dur: 0.06, gain: 0.08 });
  },
  glyph(s, t) {
    s.tone(t, { type: 'sawtooth', freq: 1200, to: 120, dur: 0.35, gain: 0.18, filter: 3000 });
    s.burst(t, { dur: 0.3, gain: 0.3, type: 'highpass', freq: 3000, attack: 0.005 });
  },
  rubble(s, t) {
    s.burst(t, { dur: 0.7, gain: 0.35, type: 'lowpass', freq: 500, to: 200, attack: 0.05 });
    for (let i = 0; i < 4; i++) s.tone(t + 0.08 + i * 0.13 + Math.random() * 0.05, { freq: 140 + Math.random() * 60, to: 70, dur: 0.1, gain: 0.35 });
  },
  unlock(s, t) {
    s.burst(t, { dur: 0.03, gain: 0.4, type: 'bandpass', freq: 2600, q: 6 });
    s.burst(t + 0.12, { dur: 0.04, gain: 0.45, type: 'bandpass', freq: 1700, q: 6 });
  },
  speech(s, t) {
    for (let i = 0; i < 3; i++) s.tone(t + i * 0.07, { type: 'triangle', freq: 260 + Math.random() * 160, dur: 0.07, gain: 0.05, attack: 0.01 });
  },
  quest(s, t) {
    s.tone(t, { freq: 784, dur: 0.6, gain: 0.14 });
    s.tone(t + 0.12, { freq: 1175, dur: 0.8, gain: 0.12 });
  },
  ominous(s, t) {
    s.tone(t, { type: 'sawtooth', freq: 73, dur: 1.2, gain: 0.14, attack: 0.3, filter: 400 });
    s.tone(t, { type: 'sawtooth', freq: 77.8, dur: 1.2, gain: 0.1, attack: 0.3, filter: 400 });
  },
  // The recording plays instead; this is the fallback sting if it can't be fetched or decoded.
  'spellstrike-fumble'(s, t) {
    RECIPES['spell-fail'](s, t, 1);
    RECIPES.bad(s, t + 0.3, 1);
  },
};
